import { useState } from "react";

type Node = {
  id: string;
  label: string;
  role: string;
  why: string;
  connects: string;
  json: string;
};

const NODES: Node[] = [
  {
    id: "organization",
    label: "Organization",
    role: "The brand entity",
    why: "This is the node search engines and assistants resolve your brand against. Everything else on the site points back to it, which is why it lives at a stable @id and never gets duplicated per page.",
    connects: "Referenced by WebSite.publisher, Article.publisher, LocalBusiness.parentOrganization, and Person.worksFor.",
    json: `{
  "@type": "Organization",
  "@id": "https://example.com/#organization",
  "name": "Your Company",
  "url": "https://example.com/",
  "logo": "https://example.com/icon-512.png",
  "sameAs": [
    "https://www.linkedin.com/company/your-company",
    "https://x.com/yourcompany"
  ]
}`,
  },
  {
    id: "localbusiness",
    label: "LocalBusiness",
    role: "The physical, findable business",
    why: "Adds the things local search cares about that Organization doesn't carry: a street address, geo coordinates, opening hours, price range, and an explicit service area.",
    connects: "parentOrganization points at the Organization @id, so the two are read as one entity rather than two competing brands.",
    json: `{
  "@type": "LocalBusiness",
  "@id": "https://example.com/#localbusiness",
  "name": "Your Company",
  "telephone": "+1-555-000-0000",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1431 Salem Meadow Cir",
    "addressLocality": "Austin",
    "addressRegion": "TX",
    "postalCode": "78745",
    "addressCountry": "US"
  },
  "geo": { "@type": "GeoCoordinates", "latitude": 30.1943, "longitude": -97.8231 },
  "parentOrganization": { "@id": "https://example.com/#organization" }
}`,
  },
  {
    id: "website",
    label: "WebSite",
    role: "The publication container",
    why: "One node per site, not per page. It tells engines who publishes the site and in what language, and is the correct home for a sitelinks search action if you have on-site search.",
    connects: "publisher points at the Organization @id.",
    json: `{
  "@type": "WebSite",
  "@id": "https://example.com/#website",
  "url": "https://example.com/",
  "name": "Your Company",
  "inLanguage": "en-US",
  "publisher": { "@id": "https://example.com/#organization" }
}`,
  },
  {
    id: "breadcrumb",
    label: "BreadcrumbList",
    role: "Where this page sits",
    why: "Cheapest structured-data win there is. It replaces the raw URL in the SERP with a readable path and gives engines an explicit hierarchy instead of one inferred from URL segments.",
    connects: "Page-level. Every deep page should have its own, matching the visible breadcrumb.",
    json: `{
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://example.com/" },
    { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://example.com/services/" },
    { "@type": "ListItem", "position": 3, "name": "Local SEO", "item": "https://example.com/services/local-seo/" }
  ]
}`,
  },
  {
    id: "service",
    label: "Service",
    role: "What you sell",
    why: "Turns a service page from prose into a described offering with a named provider and an area served. It is how a commercial page gets attached to the brand entity rather than floating free.",
    connects: "provider points at the LocalBusiness or Organization @id.",
    json: `{
  "@type": "Service",
  "name": "Local SEO",
  "serviceType": "Local search optimization",
  "provider": { "@id": "https://example.com/#localbusiness" },
  "areaServed": { "@type": "State", "name": "Texas" }
}`,
  },
  {
    id: "article",
    label: "Article / BlogPosting",
    role: "An editorial page",
    why: "Carries the two signals that matter most for E-E-A-T: who wrote it, and when it was genuinely last revised. Auto-bumping dateModified on every build is the most common way sites poison this node.",
    connects: "author points at a Person @id with a real profile page; publisher points at the Organization.",
    json: `{
  "@type": "BlogPosting",
  "headline": "How AI Overviews pick their sources",
  "datePublished": "2026-02-11",
  "dateModified": "2026-06-04",
  "author": { "@id": "https://example.com/authors/jane-doe/#person" },
  "publisher": { "@id": "https://example.com/#organization" },
  "mainEntityOfPage": "https://example.com/how-ai-overviews-pick-sources/"
}`,
  },
  {
    id: "person",
    label: "Person",
    role: "The author entity",
    why: "An author byline without a Person entity is just text. With a profile page, credentials, and sameAs links, the byline becomes something an engine can corroborate off-domain — which is what actually moves E-E-A-T.",
    connects: "Referenced by Article.author; worksFor points at the Organization.",
    json: `{
  "@type": "Person",
  "@id": "https://example.com/authors/jane-doe/#person",
  "name": "Jane Doe",
  "jobTitle": "Head of Search",
  "worksFor": { "@id": "https://example.com/#organization" },
  "knowsAbout": ["Technical SEO", "Local SEO", "Answer Engine Optimization"],
  "sameAs": ["https://www.linkedin.com/in/janedoe"]
}`,
  },
  {
    id: "faqpage",
    label: "FAQPage",
    role: "Questions answered on the page",
    why: "Only mark up questions and answers a visitor can actually read on that page. Marking up hidden or invented FAQs is the fastest route to a structured-data manual action.",
    connects: "Page-level. Pairs naturally with the answer-first blocks AI assistants extract.",
    json: `{
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does local SEO take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most local programs show Map Pack movement in 60 to 90 days."
      }
    }
  ]
}`,
  },
];

/**
 * Educational schema graph explorer. Shows how the JSON-LD nodes we ship
 * reference each other through stable @ids — the part most schema tutorials
 * skip — with a copyable snippet per node.
 */
export function SchemaGraphExplorer() {
  const [active, setActive] = useState<Node>(NODES[0]);
  const [copied, setCopied] = useState(false);

  return (
    <div className="rounded-3xl border border-border bg-card/40 p-6 md:p-10">
      <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
        <nav aria-label="Schema nodes" className="flex flex-wrap gap-2 lg:flex-col">
          {NODES.map((n) => {
            const on = n.id === active.id;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  setActive(n);
                  setCopied(false);
                }}
                aria-current={on}
                className={`rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
                  on
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                <span className="block font-medium">{n.label}</span>
                <span className="block text-xs text-muted-foreground">{n.role}</span>
              </button>
            );
          })}
        </nav>

        <div>
          <h3 className="font-display text-2xl tracking-tight">{active.label}</h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{active.why}</p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            <span className="text-primary">Connects: </span>
            {active.connects}
          </p>

          <div className="mt-6 overflow-hidden rounded-2xl border border-border/70">
            <div className="flex items-center justify-between border-b border-border/70 bg-background/60 px-4 py-2">
              <span className="font-mono text-xs text-muted-foreground">application/ld+json</span>
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard?.writeText(active.json);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="text-xs text-primary hover:underline"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="overflow-x-auto bg-background/40 p-4 text-xs leading-relaxed">
              <code className="font-mono">{active.json}</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
